/**
 * Testing Toolkit (by BrowserStack) — 15+ Essential Manual Testing Tools
 *
 * Its main web-accessible-resource entry is matched to every origin (<all_urls>) with
 * `use_dynamic_url: false`, and exposes the BrowserStack logo, the `icons/` set and the
 * `src/runtime/` scripts. A second, separate entry scopes the popup pages to
 * BrowserStack's own domains — but the first group is enough to detect from any page at
 * a fixed URL.
 *
 * The logo is the cleanest signal: `BrowserStackLogo.svg` is vendor-named and loads
 * through `probeImage` with no CORS grant. The `src/runtime/` scripts corroborate with
 * stable, non-hashed names (the `assets/` bundle files are build-hashed and avoided).
 *
 * Manifest verified against version 1.2.11.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'khkpfminnhkjeabcnhpjcfhegbbodpjk';

export const testingToolkit: ExtensionDefinition = {
  id: ID,
  name: 'Testing Toolkit: 15+ Essential Manual Testing Tools',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 20_000,
  notes:
    'Static WAR on every origin including the BrowserStack logo and src/runtime scripts. ' +
    'The vendor-named logo is conclusive and needs no CORS grant.',
  signals: [
    {
      id: 'war-browserstack-logo',
      kind: 'web-accessible-resource',
      description:
        'Load src/assets/icons/BrowserStackLogo.svg via <img> — a vendor-named logo ' +
        'exposed to every origin; needs no CORS grant, so it survives a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'src/assets/icons/BrowserStackLogo.svg'),
    },
    {
      id: 'war-runtime-content-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch src/runtime/content.js — a stable, non-hashed runtime script exposed to ' +
        'every origin.',
      weight: 0.5,
      run: () => probeFetch(ID, 'src/runtime/content.js'),
    },
    {
      id: 'war-bugcapture-content-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch src/runtime/bugCapture/content.js — the bug-capture content script, a ' +
        'third stable path independent of the logo and runtime.',
      weight: 0.5,
      run: () => probeFetch(ID, 'src/runtime/bugCapture/content.js'),
    },
  ],
};
